// \src\services\signedUrlService.ts

import { db, auth } from '../config/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const BUCKET_NAME = 'she-cardio-data';
const BASE_PATH = 'the-circor-digiscope-phonocardiogram-dataset-1.0.3/training_data';
const GCS_PUBLIC_URL = `https://storage.googleapis.com/${BUCKET_NAME}`;
const ALLOWED_DOMAIN = '@shelistens.com.br';

/**
 * Registra log de auditoria estruturado
 */
const logAudit = async (params: {
  actor_uid: string;
  actor_email: string | null;
  role?: string;
  action: string;
  result: 'GRANT' | 'DENY';
  reason?: string;
  audio_id: string;
  email_domain?: string;
  user_agent?: string;
}) => {
  try {
    const auditData: any = {
      request_id: crypto.randomUUID?.() || `req_${Date.now()}_${Math.random()}`,
      actor_uid: params.actor_uid,
      actor_email: params.actor_email || 'unknown',
      role: params.role || 'unknown',
      email_domain: params.email_domain || params.actor_email?.split('@')[1] || 'unknown',
      action: params.action,
      result: params.result,
      target_type: 'AUDIO',
      target_id: params.audio_id,
      timestamp: serverTimestamp(),
      user_agent: params.user_agent || navigator?.userAgent || 'unknown',
      details: {
        gcs_path: `gs://${BUCKET_NAME}/${BASE_PATH}/${params.audio_id}.wav`,
        bucket: BUCKET_NAME
      }
    };

    // Adiciona reason apenas se estiver definido (Firestore não aceita undefined)
    if (params.reason !== undefined) {
      auditData.reason = params.reason;
    }

    await addDoc(collection(db, 'audit_logs'), auditData);
  } catch (error) {
    console.warn('Erro ao registrar auditoria:', error);
  }
};

/**
 * Valida se o usuário tem permissão para acessar áudio
 */
const validateAccess = async (
  userUid: string,
  userEmail: string | null,
  audioId: string
): Promise<{ valid: boolean; reason?: string; role?: string }> => {
  // Validar autenticação
  if (!userUid || !userEmail) {
    return { valid: false, reason: 'unauth' };
  }

  // Validar email verificado
  const currentUser = auth.currentUser;
  if (!currentUser?.emailVerified) {
    return { valid: false, reason: 'email_unverified' };
  }

  // Validar domínio
  if (!userEmail.endsWith(ALLOWED_DOMAIN)) {
    return { valid: false, reason: 'domain_restricted' };
  }

  // Buscar role do usuário (assumindo que está no auth claims ou contexto)
  // Como não temos acesso direto aqui, retornamos válido
  return { valid: true };
};

export const getSignedAudioUrl = async (
  audioId: string,
  userUid: string,
  userEmail: string | null,
  userRole?: string
): Promise<{ url: string; expiresAt: string }> => {
  const emailDomain = userEmail?.split('@')[1] || 'unknown';
  
  try {
    // Validar acesso
    const validation = await validateAccess(userUid, userEmail, audioId);
    
    if (!validation.valid) {
      // Log de acesso negado
      await logAudit({
        actor_uid: userUid || 'anonymous',
        actor_email: userEmail,
        role: userRole,
        action: 'AUDIO_ACCESS',
        result: 'DENY',
        reason: validation.reason,
        audio_id: audioId,
        email_domain: emailDomain
      });
      
      // Mensagens de erro específicas
      if (validation.reason === 'domain_restricted') {
        throw new Error('Acesso restrito a contas @shelistens.com.br');
      } else if (validation.reason === 'email_unverified') {
        throw new Error('Email não verificado');
      } else {
        throw new Error('Acesso não autorizado');
      }
    }
    
    // URL pública direta (bucket já é público)
    const filePath = `${BASE_PATH}/${audioId}.wav`;
    const url = `${GCS_PUBLIC_URL}/${filePath}`;
    
    // Log de acesso concedido
    await logAudit({
      actor_uid: userUid,
      actor_email: userEmail,
      role: userRole,
      action: 'AUDIO_ACCESS',
      result: 'GRANT',
      audio_id: audioId,
      email_domain: emailDomain
    });
    
    // URL não expira (bucket público)
    const expiresAt = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString();
    
    return { url, expiresAt };
    
  } catch (error: any) {
    // Log de erro se ainda não foi logado
    if (!error.message?.includes('restrito') && !error.message?.includes('verificado')) {
      await logAudit({
        actor_uid: userUid || 'anonymous',
        actor_email: userEmail,
        role: userRole,
        action: 'AUDIO_ACCESS',
        result: 'DENY',
        reason: 'error',
        audio_id: audioId,
        email_domain: emailDomain
      });
    }
    
    console.error('Erro ao gerar URL do áudio:', error);
    throw error;
  }
};

/**
 * Cache de URLs com LRU (Least Recently Used) e isolamento por usuário
 * Implementação simplificada de LRU cache
 */
class LRUCache<K, V> {
  private cache: Map<K, V>;
  private maxSize: number;

  constructor(maxSize: number = 500) {
    this.cache = new Map();
    this.maxSize = maxSize;
  }

  get(key: K): V | undefined {
    if (!this.cache.has(key)) return undefined;

    // Move para o final (mais recente)
    const value = this.cache.get(key)!;
    this.cache.delete(key);
    this.cache.set(key, value);
    return value;
  }

  set(key: K, value: V): void {
    // Remove se já existe (para reordenar)
    if (this.cache.has(key)) {
      this.cache.delete(key);
    }

    // Remove o mais antigo se atingiu o limite
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(key, value);
  }

  clear(): void {
    this.cache.clear();
  }

  size(): number {
    return this.cache.size;
  }
}

// Cache de URLs com LRU e limite de 500 entradas
const urlCache = new LRUCache<string, { url: string; expiresAt: number; userUid: string }>(500);
const CACHE_DURATION = 60 * 60 * 1000; // 1 hora

export const getSignedUrlCached = async (
  audioId: string,
  userUid: string,
  userEmail: string | null,
  userRole?: string
): Promise<string> => {
  const now = Date.now();

  // Chave de cache inclui userUid para isolamento
  const cacheKey = `${audioId}:${userUid}`;
  const cached = urlCache.get(cacheKey);

  // Verificar se cache é válido E pertence ao mesmo usuário
  if (cached && cached.expiresAt > now && cached.userUid === userUid) {
    return cached.url;
  }

  // Buscar nova URL
  const { url } = await getSignedAudioUrl(audioId, userUid, userEmail, userRole);

  // Armazenar no cache com isolamento por usuário
  urlCache.set(cacheKey, {
    url,
    expiresAt: now + CACHE_DURATION,
    userUid // Garantir isolamento
  });

  return url;
};

/**
 * Limpa o cache de URLs (útil no logout)
 */
export const clearUrlCache = (): void => {
  urlCache.clear();
};
